import { useCallback, useId, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { useApiClient } from '@/api/ApiClientProvider'
import { Badge, Icon, useDismiss } from '@/design'
import { useGlobalFilters } from '@/lib/useGlobalFilters'

import { TenantMark } from './TenantMark'

/**
 * The organisation being viewed. In the real product a session belongs to one
 * tenant (another tenant lives on its own host), so there is nothing to switch
 * and this is a label; the demo client can hold several and offers the choice.
 * Switching clears entity filters, which name the previous tenant's things.
 */
export function TenantSwitcher() {
  const client = useApiClient()
  const queryClient = useQueryClient()
  const { setFilters } = useGlobalFilters()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const panelId = useId()
  const close = useCallback(() => setOpen(false), [])
  useDismiss(ref, open, close)

  const tenants = useQuery({ queryKey: ['tenants', client.kind], queryFn: () => client.tenants() })
  const current = tenants.data?.find((tenant) => tenant.current)
  const switchTenant = client.switchTenant

  if (!switchTenant || (tenants.data?.length ?? 0) < 2) {
    return (
      <div className="ci-tenant">
        <TenantMark tenant={current} size={24} />
        <span className="ci-tenant__name">{current?.name}</span>
      </div>
    )
  }

  return (
    <div className="ci-tenant" ref={ref}>
      <button
        type="button"
        className="ci-button ci-tenant__button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((value) => !value)}
      >
        <TenantMark tenant={current} size={22} />
        <span className="ci-tenant__name">{current?.name}</span>
        <span className="ci-visually-hidden">, switch organisation</span>
        <Icon name="chevronDown" size={14} />
      </button>
      {open ? (
        <div id={panelId} className="ci-popover ci-tenant__panel">
          <p className="ci-popover__label">Switch organisation</p>
          <ul className="ci-popover__list">
            {tenants.data?.map((tenant) => (
              <li key={tenant.id}>
                <button
                  type="button"
                  className="ci-popover__item"
                  aria-current={tenant.current ? 'true' : undefined}
                  onClick={async () => {
                    setOpen(false)
                    if (tenant.current) return
                    await switchTenant(tenant.id)
                    setFilters((filters) => ({ ...filters, dimensions: {} }))
                    await queryClient.resetQueries()
                  }}
                >
                  <TenantMark tenant={tenant} size={22} />
                  <span>{tenant.name}</span>
                  {tenant.current ? <Badge tone="accent">Current</Badge> : null}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  )
}
